import React from 'react';
import ComplianceBadge from './compliance/ComplianceBadge';
import { fmtDate, fmtDateTime } from '../lib/dates';
import { formatGBP as gbp } from '../lib/money';
import AwardMedal from './AwardMedal';

// Awarded-winner banner — mounts at the top of the Recipients &
// Responses section when a brief is in the awarded state
// (status='closed' && awarded_supplier_id is set). Replaces the
// winning supplier's row in the regular list so the same supplier
// doesn't render twice; non-winning rows pick up a muted treatment
// in the parent.
//
// Redesigned around the awarded-card inspiration: AWARDED pill with
// trophy icon top-left, attribution line ("Awarded {date} by {name}"),
// supplier name + compliance badge on a title row, three-column stat
// sub-card (Earliest Delivery / Quote Type / Payment Schedule with
// its caveat text), action buttons on the footer with Re-open pushed
// right as a destructive action.
//
// Data sources (all from BriefDetail's existing payload):
//   brief        — for awarded_amount, closed_at, closed_by_user
//   winner       — the awarded recipient row + its suppliers join
//   winnerResp   — the brief_responses row matching the winner
//                  (may be null if the supplier was awarded without
//                  a recorded response — defensive)
//   avgOther     — average total_price of other responses, for the
//                  "£X below average" sub-line. null when there
//                  are no other quotes to compare against.
//   onMessage / onViewQuote / onDownload / onReopen — handlers
//     wired up by the parent; the panel is presentational.

const formatGBP = (n) => gbp(n, { fallback: '£0' });
const formatGBPLong = (n) => gbp(n, { pence: 'auto', fallback: '£0' });
// Long-form date + 24h time, matching the inspiration's "3 Jun 2026,
// 21:14" treatment. dateStyle:'short' gave "03/06/2026" which read as
// data rather than a human attribution line — long-form lands cleaner
// next to the supplier name above.
const formatDateTime = (iso) => fmtDateTime(iso);
const formatDate = (iso) => fmtDate(iso);
const truncate = (s, n) => {
  const str = (s || '').toString().trim();
  if (!str) return '—';
  if (str.length <= n) return str;
  return str.slice(0, n - 1).trimEnd() + '…';
};

export default function WinnerBanner({
  brief,
  winner,
  winnerResp,
  avgOther,
  onMessage,
  onViewQuote,
  onDownload,
  onReopen,
}) {
  if (!brief || !winner) return null;

  const supplier = winner.suppliers || {};
  const supplierName = supplier.supplier_name || 'Awarded supplier';
  const awarded = Number(brief.awarded_amount) || 0;
  const delta = avgOther != null && isFinite(avgOther) ? avgOther - awarded : null;
  const closedAtLabel = formatDateTime(brief.closed_at);
  const closer = brief.closed_by_user?.full_name || null;

  // Payment-schedule split: the schedule string often reads "100% on
  // award" / "50% deposit, 50% on delivery" etc. Show the headline
  // value (first percentage or first short phrase) prominently and
  // the rest as caveat copy beneath, mirroring the inspiration's
  // "100% / 100% payment upon awarding of the brief" treatment.
  const paymentSchedule = (winnerResp?.payment_schedule || '').toString().trim();
  const paymentHeadline = (() => {
    if (!paymentSchedule) return null;
    const pct = paymentSchedule.match(/^\s*(\d{1,3}\s*%)/);
    if (pct) return pct[1].replace(/\s+/g, '');
    const firstClause = paymentSchedule.split(/[,.;]/)[0].trim();
    return truncate(firstClause, 24);
  })();
  const paymentCaveat = paymentSchedule && paymentHeadline && paymentSchedule !== paymentHeadline
    ? paymentSchedule
    : null;

  // The meta line — verification, quote type, awarding user — joined with " · ".
  // EMPTY FIELDS ARE OMITTED, not rendered as em-dashes. That is the change the
  // brief calls the substantive one, and it is why the old three-column stat
  // card has gone: two of its three cells were usually a dash.
  const verificationLabel = supplier.compliance_status === 'green' ? 'Verified'
    : supplier.compliance_status === 'amber' ? 'Compliance expiring soon'
      : supplier.compliance_status === 'red' ? 'Compliance outstanding'
        : null;
  const metaParts = [
    verificationLabel,
    winnerResp ? (winnerResp.is_itemised ? 'itemised quote' : 'lump-sum quote') : null,
    closer ? `by ${closer}` : null,
  ].filter(Boolean);

  // Facts that only appear when they hold something.
  const deliveryLabel = winnerResp?.deadline_date ? formatDate(winnerResp.deadline_date) : null;

  return (
    // Option C. ONE accent left on the card: a 1px --seal-soft border.
    // Deleted with this rebuild, per the brief: the AWARDED pill, the trophy
    // glyph, the glossy hexagon tick, the green left border, and the tinted
    // gradient background. All five were saying the same thing the medal now
    // says once.
    <div id="brief-winner-banner" className="relative bg-white border rounded-2xl overflow-hidden mb-6 font-['Metropolis']" style={{ borderColor: 'var(--seal-soft)' }}>
      <div className="px-6 py-5 flex items-start gap-4 flex-wrap">
        <AwardMedal size={40} />

        <div className="flex-1 min-w-[200px]">
          <span className="block text-[11px] font-semibold uppercase" style={{ letterSpacing: '0.09em', color: 'var(--seal)' }}>
            Awarded · {closedAtLabel}
          </span>
          <div className="flex items-center gap-2.5 flex-wrap mt-1">
            <h3 className="text-[19px] font-semibold text-ink" style={{ letterSpacing: '-0.01em' }}>{supplierName}</h3>
            <ComplianceBadge state={supplier.compliance_status} size="sm" />
          </div>
          {/* One meta line, empty parts dropped. */}
          {metaParts.length > 0 && (
            <p className="text-[13px] mt-1" style={{ color: 'var(--ink-mid, #4A5A4E)' }}>{metaParts.join(' · ')}</p>
          )}
          {/* Facts that used to be a three-cell grid of em-dashes. Each one
              renders ONLY when it holds a value — no label, no row, no
              placeholder otherwise. */}
          {(deliveryLabel || paymentHeadline) && (
            <p className="text-[13px] mt-1" style={{ color: 'var(--ink-mid, #4A5A4E)' }}>
              {[
                deliveryLabel ? `Earliest delivery ${deliveryLabel}` : null,
                paymentHeadline ? `Payment ${paymentHeadline}` : null,
              ].filter(Boolean).join(' · ')}
            </p>
          )}
          {paymentCaveat && (
            <p className="text-[12px] mt-1 leading-snug" style={{ color: 'var(--ink-faint, #7C8880)' }}>{paymentCaveat}</p>
          )}
        </div>

        <div className="text-left md:text-right">
          <div className="text-[30px] font-semibold text-ink leading-[1.1]" style={{ letterSpacing: '-0.02em' }}>
            {formatGBPLong(awarded)}
          </div>
          <div className="text-[12px]" style={{ color: 'var(--ink-faint, #7C8880)' }}>
            net · ex-VAT
          </div>
          {delta != null && delta > 0 && (
            <div className="text-[12px] mt-0.5" style={{ color: 'var(--seal)' }}>{formatGBP(delta)} below average quote</div>
          )}
          {delta != null && delta < 0 && (
            <div className="text-[12px] mt-0.5 text-[#7A4E10]">{formatGBP(Math.abs(delta))} above average quote</div>
          )}
        </div>
      </div>

      {/* Action buttons — Message Supplier is the single primary
          action (filled brand-lime, same treatment as "+ Create
          Brief"); View Full Quote + Download Quote are secondary
          ghost buttons; Re-open Brief is destructive (red outline)
          and sits pushed-right via ml-auto.

          Message Supplier stays enabled on an awarded brief even
          when no contact email is on file — the click handler can
          fall back to a copy-prompt / toast rather than the button
          appearing dead. The hover title still flags the missing
          email so the operator knows why no mailto opens. */}
      <div className="px-6 pb-5 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={onMessage}
          title={supplier.main_contact_email ? `Email ${supplier.main_contact_name || supplier.supplier_name}` : 'No contact email on file — copy and contact directly'}
          className="text-[13px] font-semibold rounded-[10px] px-3.5 py-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          style={{ background: '#B5DE7A', border: '1px solid #B5DE7A', color: '#1C3A2A', outlineColor: 'var(--seal)' }}>
          Message supplier
        </button>
        <button
          type="button"
          onClick={onViewQuote}
          disabled={!winnerResp}
          className="text-[13px] font-medium rounded-[10px] px-3.5 py-2 bg-transparent border transition-colors hover:bg-[#FAFCF5] disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          style={{ borderColor: '#C3CEB4', color: '#1C3A2A', outlineColor: 'var(--seal)' }}>
          View quote
        </button>
        <button
          type="button"
          onClick={onDownload}
          className="text-[13px] font-medium rounded-[10px] px-3.5 py-2 bg-transparent border transition-colors hover:bg-[#FAFCF5] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          style={{ borderColor: '#C3CEB4', color: '#1C3A2A', outlineColor: 'var(--seal)' }}>
          Download
        </button>
        {/* Re-open brief is TERTIARY, not red. It is reversible and not
            destructive — the red outline was overstating it, and red beside a
            just-awarded supplier reads as something having gone wrong. */}
        <button
          type="button"
          onClick={onReopen}
          className="ml-auto text-[13px] font-medium rounded-[10px] px-3.5 py-2 bg-transparent border border-transparent transition-colors hover:bg-[#FAFCF5] hover:border-[#DDE4D2] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          style={{ color: 'var(--ink-mid, #4A5A4E)', outlineColor: 'var(--seal)' }}>
          Re-open brief
        </button>
      </div>
    </div>
  );
}
